import React from 'react';
import { Helmet } from 'react-helmet';

interface EnhancedSEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  imageAlt?: string;
  url?: string;
  type?: 'website' | 'article' | 'event';
  publishedTime?: string;
  modifiedTime?: string;
  
  // 旅游特定属性
  locationName?: string;
  locationAddress?: string;
  latitude?: number;
  longitude?: number;
  priceRange?: string;
  ratingValue?: number;
  ratingCount?: number;
  openingHours?: string;
}

const EnhancedSEO: React.FC<EnhancedSEOProps> = ({ 
  title, 
  description, 
  keywords = '',
  image = '',
  imageAlt = '',
  url = '',
  type = 'website',
  publishedTime = '',
  modifiedTime = '',
  
  // 旅游特定
  locationName = 'Hong Kong',
  locationAddress = 'Hong Kong',
  latitude = 22.3193,
  longitude = 114.1694,
  priceRange = '$$',
  ratingValue = 4.8,
  ratingCount = 125,
  openingHours = 'Mo-Su 00:00-23:59'
}) => {
  const siteName = "SSCIKOL";
  const defaultImage = "https://sscikol.com/og-image.jpg";
  const siteUrl = "https://sscikol.com";
  const twitterHandle = "@sscikol";
  
  const metaTitle = `${title} | ${siteName}`;
  const metaDescription = description;
  const metaImage = image || defaultImage;
  const metaUrl = url || siteUrl;
  const metaKeywords = keywords || 'yacht, Hong Kong, sailing, marine,游艇,香港,航海,海洋,AI friendly,LLM optimized';
  
  // AI/LLM特定元标签
  const aiMetaTags = {
    'ai-content-depth': 'intermediate',
    'ai-content-freshness': 'seasonally_updated',
    'ai-target-audience': 'yacht owners, sailing enthusiasts, Hong Kong residents, tourists',
    'ai-content-format': 'multimedia',
    'ai-readability': 'high',
    'ai-structured-data': 'rich'
  };
  
  // 生成主结构化数据 - 旅游目的地
  const getTourismStructuredData = () => {
    const baseData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": siteName,
      "description": "SSCIKOL - SSCI领域KOL导航网站 with rich content and AI optimization",
      "url": siteUrl,
      "image": metaImage,
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "Hong Kong",
        "addressCountry": "CN",
        "addressLocality": locationName,
        "streetAddress": locationAddress
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": latitude,
        "longitude": longitude
      },
      "openingHours": openingHours,
      "priceRange": priceRange,
      "telephone": "",
      "email": "info@sscikol.com",
      "sameAs": [
        "https://twitter.com/sscikol",
        "https://www.instagram.com/sscikol",
        "https://www.facebook.com/sscikol"
      ]
    };
    
    // 如果是文章页面
    if (type === 'article') {
      const articleData = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": metaTitle,
        "description": metaDescription,
        "image": metaImage,
        "author": {
          "@type": "Organization",
          "name": siteName
        },
        "publisher": {
          "@type": "Organization",
          "name": siteName,
          "logo": {
            "@type": "ImageObject",
            "url": "https://sscikol.com/logo.png"
          }
        },
        "datePublished": publishedTime || new Date().toISOString(),
        "dateModified": modifiedTime || new Date().toISOString(),
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": metaUrl
        }
      };
      
      return articleData;
    }
    
    // 如果是活动页面
    if (type === 'event') {
      const eventData = {
        "@context": "https://schema.org",
        "@type": "Event",
        "name": metaTitle,
        "description": metaDescription,
        "image": metaImage,
        "startDate": publishedTime || new Date().toISOString(),
        "endDate": modifiedTime || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        "eventStatus": "https://schema.org/EventScheduled",
        "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
        "location": {
          "@type": "Place",
          "name": locationName,
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "Hong Kong",
            "addressCountry": "CN"
          }
        },
        "organizer": {
          "@type": "Organization",
          "name": siteName,
          "url": siteUrl
        }
      };
      
      return eventData;
    }
    
    return baseData;
  };
  
  // 生成评分结构化数据
  const getAggregateRatingData = () => {
    return {
      "@context": "https://schema.org",
      "@type": "AggregateRating",
      "itemReviewed": {
        "@type": "Organization",
        "name": siteName,
        "url": siteUrl
      },
      "ratingValue": ratingValue,
      "ratingCount": ratingCount,
      "bestRating": 5,
      "worstRating": 1,
      "author": {
        "@type": "Organization",
        "name": "Travelers Community"
      }
    };
  };
  
  // 生成旅游路线结构化数据
  const getTouristTripData = () => {
    return {
      "@context": "https://schema.org",
      "@type": "TouristTrip",
      "name": "Xinjiang Highlights Tour",
      "description": "7-day guided tour covering the best attractions in Xinjiang",
      "itinerary": {
        "@type": "ItemList",
        "numberOfItems": 7,
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "item": {
              "@type": "Organization",
              "name": "Urumqi - Xinjiang Regional Museum",
              "description": "Learn about Xinjiang's history and cultures"
            }
          },
          {
            "@type": "ListItem",
            "position": 2,
            "item": {
              "@type": "Organization",
              "name": "Heavenly Lake (Tian Chi)",
              "description": "Stunning alpine lake in the Tianshan Mountains"
            }
          },
          {
            "@type": "ListItem",
            "position": 3,
            "item": {
              "@type": "Organization",
              "name": "Turpan - Flaming Mountains",
              "description": "Famous red sandstone mountains"
            }
          }
        ]
      },
      "offers": {
        "@type": "Offer",
        "price": "1200",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": `${siteUrl}/tours`
      }
    };
  };
  
  // 生成FAQ结构化数据
  const getFAQData = () => {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is the best time to visit Xinjiang?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The best time to visit Xinjiang is from May to October when the weather is pleasant and most attractions are accessible. Spring (April-May) offers beautiful flowers, while autumn (September-October) provides stunning fall colors."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need a special permit to visit Xinjiang?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most areas of Xinjiang are open to tourists without special permits. However, some border regions may require additional permits. It's recommended to check with local authorities or travel agencies for the latest requirements."
          }
        },
        {
          "@type": "Question",
          "name": "What are the must-see attractions in Xinjiang?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Top attractions include: 1) Heavenly Lake (Tian Chi), 2) Kanas Lake, 3) Flaming Mountains in Turpan, 4) Ancient City of Kashgar, 5) Taklamakan Desert, 6) Tianshan Mountains, and 7) Xinjiang Regional Museum in Urumqi."
          }
        },
        {
          "@type": "Question",
          "name": "Is Xinjiang safe for tourists?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, Xinjiang is generally safe for tourists. The region has significant security presence, and tourism infrastructure is well-developed. As with any travel, take normal precautions and follow local guidelines."
          }
        },
        {
          "@type": "Question",
          "name": "What is the local cuisine like in Xinjiang?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Xinjiang cuisine features Central Asian influences with highlights including: lamb kebabs, pilaf (抓饭), naan bread, laghman noodles, samsa (baked buns), and various dairy products. The food is known for its robust flavors and use of spices."
          }
        }
      ]
    };
  };
  
  // 生成网站结构化数据
  const getWebSiteData = () => {
    return {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": siteName,
      "url": siteUrl,
      "description": "Official travel guide to Xinjiang, China - Discover attractions, culture, food, and travel tips",
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${siteUrl}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string"
      },
      "inLanguage": ["en", "zh-CN"],
      "author": {
        "@type": "Organization",
        "name": siteName,
        "url": siteUrl
      }
    };
  };

  return (
    <Helmet>
      {/* ========== 基础meta标签 ========== */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      
      {/* ========== AI/LLM特定meta标签 ========== */}
      <meta name="ai-content-type" content="product_service" />
      <meta name="ai-content-depth" content={aiMetaTags['ai-content-depth']} />
      <meta name="ai-content-freshness" content={aiMetaTags['ai-content-freshness']} />
      <meta name="ai-target-audience" content={aiMetaTags['ai-target-audience']} />
      <meta name="ai-content-format" content={aiMetaTags['ai-content-format']} />
      <meta name="ai-readability" content={aiMetaTags['ai-readability']} />
      <meta name="ai-structured-data" content={aiMetaTags['ai-structured-data']} />
      
      {/* ========== Open Graph (Facebook) ========== */}
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type === 'article' ? 'article' : 'website'} />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt || description.substring(0, 100)} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="zh_CN" />
      
      {/* 文章特定OG标签 */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      <meta property="article:author" content={siteName} />
      <meta property="article:section" content="Travel" />
      <meta property="article:tag" content="Xinjiang,Travel,China,Tourism" />
      
      {/* ========== Twitter Card ========== */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:image:alt" content={imageAlt || description.substring(0, 100)} />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:label1" content="Destination" />
      <meta name="twitter:data1" content="Xinjiang, China" />
      <meta name="twitter:label2" content="Category" />
      <meta name="twitter:data2" content="Travel & Tourism" />
      
      {/* ========== 结构化数据 ========== */}
      {/* 主结构化数据 - 旅游目的地 */}
      <script type="application/ld+json">
        {JSON.stringify(getTourismStructuredData())}
      </script>
      
      {/* 评分结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify(getAggregateRatingData())}
      </script>
      
      {/* 旅游路线结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify(getTouristTripData())}
      </script>
      
      {/* FAQ结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify(getFAQData())}
      </script>
      
      {/* 网站结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify(getWebSiteData())}
      </script>
      
      {/* ========== 链接标签 ========== */}
      <link rel="canonical" href={metaUrl} />
      
      {/* 多语言/本地化 */}
      <link rel="alternate" hrefLang="en" href={metaUrl} />
      <link rel="alternate" hrefLang="zh" href={`${siteUrl}/zh`} />
      <link rel="alternate" hrefLang="x-default" href={siteUrl} />
      
      {/* 预连接/预加载 */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="//www.google.com" />
      
      {/* ========== 图标 ========== */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/site.webmanifest" />
      <meta name="theme-color" content="#3b82f6" />
      
      {/* ========== 其他优化 ========== */}
      <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      
      {/* 性能提示 */}
      <meta httpEquiv="x-ua-compatible" content="ie=edge" />
      <meta name="generator" content="Vite + React with AI-Optimized SEO for Tourism" />
      
      {/* 内容安全策略提示 */}
      <meta httpEquiv="Content-Security-Policy" content="upgrade-insecure-requests" />
    </Helmet>
  );
};

export default EnhancedSEO;